import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { LocalStorageService } from '@services/local-storage.service';
import { pickClientCode } from '@shared/utils/pick-client-code.utils';
import { filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { departmentsActions } from '../departments/departments.actions';
import { selectReadAccessClientCodes } from '../licenses/licenses.selectors';
import { selectIsStoreInitialized } from '../life-cycle/life-cycle.selectors';
import { activeClientActions } from './active-client.actions';
import { selectActiveClientCode } from './active-client.selectors';
import { CLIENT_CODE_STORAGE_KEY } from './client-code-storage.key';

@Injectable()
export class ActiveClientEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly localStorage = inject(LocalStorageService);

  hydrate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(activeClientActions.hydrate),
      switchMap(() =>
        this.store.select(selectIsStoreInitialized).pipe(
          filter((isStoreInitialized) => isStoreInitialized),
          take(1),
          withLatestFrom(this.store.select(selectReadAccessClientCodes), this.store.select(selectActiveClientCode)),
        ),
      ),
      filter(
        ([, readAccessClientCodes, activeClientCode]) =>
          !activeClientCode || !readAccessClientCodes.includes(activeClientCode),
      ),
      map(([, readAccessClientCodes]) =>
        pickClientCode(
          readAccessClientCodes,
          this.localStorage.get(CLIENT_CODE_STORAGE_KEY),
          environment.commonClientCode,
        ),
      ),
      filter((clientCode): clientCode is string => !!clientCode),
      map((clientCode) => activeClientActions.setActiveClient({ clientCode })),
    ),
  );

  persist$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(activeClientActions.setActiveClient),
        tap(({ clientCode }) => this.localStorage.set(CLIENT_CODE_STORAGE_KEY, clientCode)),
      ),
    { dispatch: false },
  );

  loadDepartments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(activeClientActions.setActiveClient),
      map(({ clientCode }) => departmentsActions.ensureClientDepartments({ clientCode })),
    ),
  );
}
